import { useMediaQuery, ArrowRightIcon } from '@ag1/ui';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { clickPurchaseIntentEvent } from 'analytics/events/studio';
import { useScrollPaddingTopHandler } from 'atoms/scrollPaddingTopAtom';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { usePageContext } from 'processes/Page/PageContext';
import { useCallback, useMemo, useRef } from 'react';
import { urlFor } from 'shared/utils/urlFor';
import { useGoToOffersV2 } from '../../../../hooks/useGoToOffersV2';
import {
  ContentBlockButton,
  ContentBlockProps,
  useContentBlockBackgroundColor,
  useContentBlockField,
  useContentBlockToggle,
  useSanityImageFromCustomContent
} from '../../shared';
import styles from './OfferAnchor.module.scss';

type Props = ContentBlockProps;

export const OfferAnchor: React.FC<Props> = ({ slug, customContent, visibleOnPageLoad }) => {
  const { offer, offerCTALink, sectionsUsed, offerInvalidReason, disablePrimaryCTAButtons, page } =
    usePageContext();
  const showAGLogo = useContentBlockToggle(customContent, 'banner-logo', { slug });
  const scrollToOfferSection = useContentBlockToggle(customContent, 'scroll-to-offer-section', {
    slug
  });
  const isBannerClickable = useContentBlockToggle(customContent, 'banner-clickable', { slug });

  const headline =
    useContentBlockField(customContent, 'banner-headline', { slug }) ||
    offer?.headline ||
    'Welcome to AG1';

  const subTitle = useContentBlockField(customContent, 'banner-subtitle', { slug });

  const productImage = useSanityImageFromCustomContent(customContent, { slug });
  const imageComponent = useMemo(
    () =>
      productImage && (
        <Image
          className={styles.image}
          src={productImage.src}
          width={50}
          height={50}
          alt=""
          loading={visibleOnPageLoad ? 'eager' : 'lazy'}
          priority={visibleOnPageLoad}
        />
      ),
    [productImage, visibleOnPageLoad]
  );

  const buttonLabel =
    useContentBlockField(customContent, 'button-label', { slug }) || 'Get Your AG1';

  // not directly editable through page builder
  // this custom content is currently manually inserted for gifting lander page ONLY
  // to override checkout_flow for an existing offer
  // (which gifting lander page has none)
  const buttonUrl = useContentBlockField(customContent, 'button-url', { slug }) || offerCTALink;

  const atSmall = useMediaQuery('s');
  const mobileHeaderRef = useRef<HTMLDivElement | null>(null);
  const desktopHeaderRef = useRef<HTMLDivElement | null>(null);

  const hideOnMobile = false;
  const { height, top } = useScrollPaddingTopHandler(
    { type: 'offer-anchor', disable: !atSmall && hideOnMobile },
    mobileHeaderRef,
    desktopHeaderRef
  );

  const logoImage = useMemo(
    () =>
      urlFor({
        asset: {
          _ref: 'image-e121a703db20054e3f63e66284bcd4f56b60af90-134x55-svg',
          _type: 'reference'
        }
      }).url,
    []
  );

  const { goToOffer } = useGoToOffersV2({ withScroll: scrollToOfferSection });

  const useOnClickButton = useMemo(() => {
    if (page?.use_pdp && page?.pdp_url) {
      return false;
    }

    // Scroll if custom content says we should and we're using an offer section.
    if (scrollToOfferSection && (sectionsUsed?.builderOffer || sectionsUsed?.sanityOffer)) {
      return true;
    }
    // Scroll to builder offer section if it's invalid for some reason
    if (sectionsUsed?.builderOffer) {
      return !!offerInvalidReason || disablePrimaryCTAButtons;
    }

    return !!sectionsUsed?.sanityOffer;
  }, [
    scrollToOfferSection,
    sectionsUsed?.builderOffer,
    sectionsUsed?.sanityOffer,
    offerInvalidReason,
    disablePrimaryCTAButtons,
    page
  ]);
  const store = useStoreValue();
  const onClickButton = useCallback(() => {
    // Always track the click event, even if we're using a direct href link
    // instead of goToOffer.
    clickPurchaseIntentEvent({
      store,
      clickText: buttonLabel,
      location: 'OfferBanner',
      url: useOnClickButton ? undefined : buttonUrl || ''
    });

    if (useOnClickButton) {
      goToOffer();
    }
  }, [goToOffer, useOnClickButton, buttonLabel, buttonUrl, store]);

  const bgColor = useContentBlockBackgroundColor(customContent, {
    slug,
    fallback: 'white'
  });

  const asStackableHeaderGroupItem = sectionsUsed?.navigation || false;
  const headerStyles = useMemo<React.CSSProperties>(() => {
    if (asStackableHeaderGroupItem) {
      return { position: `relative`, top: 0 };
    }
    return { top };
  }, [asStackableHeaderGroupItem, top]);

  return (
    <div
      className={styles.root}
      style={{
        ...(isBannerClickable ? { cursor: 'pointer' } : {}),
        paddingTop: `${asStackableHeaderGroupItem ? 0 : height}px`
      }}
      onClick={isBannerClickable ? onClickButton : undefined}
      onKeyDown={
        isBannerClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClickButton();
            }
          : undefined
      }
      role="button"
      tabIndex={0}>
      <div
        ref={mobileHeaderRef}
        className={clsx(
          styles.mobile,
          bgColor === 'white' && styles.white,
          bgColor === 'grey' && styles.grey,
          bgColor === 'stone' && styles.stone
        )}
        style={headerStyles}>
        <div className={clsx(styles.primaryMobileButtonInner)}>
          {imageComponent}
          {showAGLogo && logoImage && !asStackableHeaderGroupItem && (
            <Link href="/">
              <img className={styles.logoMobile} src={logoImage} alt="logo" />
            </Link>
          )}
          <div className={styles.titleWrapperMobile}>
            <p className={styles.headlineMobile}>{headline}</p>
            <p className={styles.subTitleMobile}>{subTitle}</p>
          </div>
          <div className={styles.titleWrapperTablet}>
            <p className={styles.headlineTablet}>{headline}</p>
            <p className={styles.subTitleTablet}>{subTitle}</p>
          </div>
          {/* <p className={styles.subTitleTablet}>{subTitle}</p> */}
          <ContentBlockButton
            className={clsx(
              styles.buttonMobile
              // primaryButtonUnderLarge && styles.primaryMobileButton,
              // primaryButtonUnderLarge && unifiedButton?.hoverColor === 'dark' && styles.dark,
              // !primaryButtonUnderLarge && unifiedButton?.hoverColor === 'dark' && styles.darkLink
            )}
            button={{
              label: buttonLabel,
              link: useOnClickButton ? undefined : buttonUrl || ''
            }}
            ignoreLinkDisable={useOnClickButton}
            customization={{
              variant: 'primary',
              size: 'small',
              onClick: onClickButton
            }}
          />
        </div>
      </div>
      <div
        ref={desktopHeaderRef}
        className={clsx(
          styles.desktop,
          bgColor === 'white' && styles.white,
          bgColor === 'grey' && styles.grey,
          bgColor === 'stone' && styles.stone
        )}
        style={headerStyles}>
        {showAGLogo && logoImage && !asStackableHeaderGroupItem ? (
          <Link href="/">
            <img className={styles.logo} src={logoImage} alt="logo" />
          </Link>
        ) : (
          <div className={styles.logoPlaceholder} />
        )}
        <div className={styles.title}>
          {imageComponent}
          <p className={styles.headline}>{headline}</p>
          <p className={styles.subTitle}>{subTitle}</p>
        </div>
        <ContentBlockButton
          className={styles.button}
          button={{
            label: buttonLabel,
            link: useOnClickButton ? undefined : buttonUrl || ''
          }}
          ignoreLinkDisable={useOnClickButton}
          customization={{
            variant: 'primary',
            icon: <ArrowRightIcon />,
            iconPosition: 'right',
            size: 'small',
            onClick: onClickButton
          }}
        />
      </div>
    </div>
  );
};
